/**
 * Presentation rules for step 1 (f10.4) -- labels, pills and small formats.
 *
 * Nothing here decides anything: matching, cascades, figures and every
 * capability come from the server. These helpers only choose how a value the
 * server sent is spoken about.
 */

import { ApiError, type UsEngagementsReview } from '@/api/client';

/** Decision code -> pill class. An unknown code renders neutrally. */
const DECISION_PILLS: Readonly<Record<string, string>> = {
  UNDECIDED: 'pill-warning',
  CONFIRM: 'pill-success',
  MAP: 'pill-info',
  EXCLUDE: 'pill-neutral',
  EXCLUDED_BY_PARENT: 'pill-neutral',
  MATCHED: 'pill-success',
};

export function decisionPill(code: string): string {
  return `badge ${DECISION_PILLS[code] ?? 'pill-neutral'}`;
}

export const UNDECIDED = 'UNDECIDED';
export const EXCLUDED_BY_PARENT = 'EXCLUDED_BY_PARENT';

/** A decision that can be taken back to `UNDECIDED`. */
export function isReversible(code: string): boolean {
  return code !== UNDECIDED && code !== EXCLUDED_BY_PARENT;
}

/** The rows a value came from: the first few, then a count of the rest. */
export function formatSourceRows(rows: readonly number[], shown = 6): string {
  if (rows.length === 0) return '—';
  const head = rows.slice(0, shown).join(', ');
  const rest = rows.length - shown;
  return rest > 0 ? `${head} … +${rest}` : head;
}

export type IssueSeverity = 'ERROR' | 'WARNING' | 'INFO';

export function severityPill(severity: string): string {
  if (severity === 'ERROR') return 'badge pill-danger';
  if (severity === 'WARNING') return 'badge pill-warning';
  return 'badge pill-info';
}

export interface Transformation {
  ok: boolean;
  text: string;
}

/**
 * The "Transformations applied" list, stated from the batch's own facts.
 * Only what the parse job actually does is listed (design D4).
 */
export function transformations(review: UsEngagementsReview): Transformation[] {
  const { batch, figures, issues } = review;
  const unresolvedUnits = issues.items.filter((issue) => issue.code === 'unit_unresolved').length;
  const dropped = Math.max(0, (batch.rows_dropped ?? 0) - figures.rows.duplicates_collapsed);
  const list: Transformation[] = [
    {
      ok: true,
      text:
        'Columns bound by header name — Element: Reference, Account, Engagement, ' +
        'Account: SAPID, Proposal: Unit',
    },
    {
      ok: true,
      text: `${figures.rows.duplicates_collapsed.toLocaleString()} identical rows collapsed on Element: Reference`,
    },
    {
      ok: true,
      text: 'Accounts matched by SAP ID first, then by normalised name or alias — never fuzzily',
    },
  ];
  if (dropped > 0) {
    list.push({ ok: false, text: `${dropped} rows dropped with a warning — listed under issues` });
  }
  if (unresolvedUnits > 0) {
    list.push({
      ok: false,
      text: `${unresolvedUnits} Proposal: Unit spellings matched no Business Unit — shown under their raw spelling`,
    });
  }
  if (batch.rejection_reason) {
    list.push({ ok: false, text: batch.rejection_reason });
  }
  return list;
}

/** The server's refusal as one sentence, with its per-item problems appended. */
export function refusalMessage(error: unknown): string | null {
  if (!error) return null;
  if (error instanceof ApiError) {
    const details = error.errors.map((item) => item.message).filter(Boolean);
    return details.length > 1 ? `${error.message} ${details.join(' · ')}` : error.message;
  }
  return error instanceof Error ? error.message : 'The request could not be completed.';
}

/** The problem the server bound to one field, if any. */
export function fieldProblem(error: unknown, key: string): string | null {
  if (!(error instanceof ApiError)) return null;
  return error.errors.find((item) => item.key === key)?.message ?? null;
}

/** Batch states in which the step still owes the user a file. */
export function needsNewFile(stateCode: string): boolean {
  return stateCode === 'DISCARDED' || stateCode === 'REJECTED';
}

/** Batch states whose staged items and figures are worth showing. */
export function hasStagedItems(stateCode: string): boolean {
  return ['STAGED', 'AWAITING_RESOLUTION', 'COMMITTED'].includes(stateCode);
}

/** Batch states the parse job has not finished with yet. */
export function isParsing(stateCode: string): boolean {
  return stateCode === 'RECEIVED' || stateCode === 'PARSING';
}
